import { useState } from "react";
import { Gift, CheckCircle2 } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { trackConversion } from "@/lib/analytics";
import { useFormSubmit, submitBtnCls } from "@/lib/forms";
import { VipFields, VipConsent } from "@/components/vip/VipFields";

const initial = { firstName: "", mobile: "", email: "", birthdayMonth: "", consent: false };

const VipForm = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initial);
  const { status, submit } = useFormSubmit("vip-signup", () => {
    trackConversion("email_signup", { channel: "email_sms" });
    trackConversion("sms_signup", { channel: "email_sms" });
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (status === "success") {
    return (
      <div className="flex items-start gap-3 text-hacienda-cream" role="status" data-testid="vip-success">
        <CheckCircle2 className="text-hacienda-amber flex-shrink-0" size={28} aria-hidden="true" />
        <p className="text-lg">{t("vip.success")}</p>
      </div>
    );
  }

  const onSubmit = (e) => {
    e.preventDefault();
    if (form.consent) submit({ ...form, birthdayMonth: form.birthdayMonth || null, language: lang });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="vip-form">
      <VipFields form={form} update={update} />
      <VipConsent checked={form.consent} onChange={(v) => update("consent", v)} />
      {status === "error" && <p className="text-red-300 text-sm" role="alert" data-testid="vip-error">{t("vip.error")}</p>}
      <button type="submit" disabled={!form.consent || status === "loading"} className={`w-full sm:w-auto px-8 py-3 ${submitBtnCls} focus-visible:outline-hacienda-amber`} data-testid="vip-submit-btn">
        {status === "loading" ? t("actions.sending") : t("vip.submit")}
      </button>
    </form>
  );
};

export const VipSignup = () => {
  const { t } = useLang();
  return (
    <section className="py-20 px-4 bg-hacienda-charcoal text-hacienda-cream" data-testid="vip-section">
      <div className="max-w-5xl mx-auto grid md:grid-cols-5 gap-10 items-center">
        <div className="md:col-span-2">
          <Gift size={36} className="text-hacienda-amber mb-4" aria-hidden="true" />
          <h2 className="font-serif text-4xl md:text-5xl font-semibold mb-4 leading-tight">{t("vip.title")}</h2>
          <p className="text-hacienda-cream/80 text-lg leading-relaxed">{t("vip.copy")}</p>
        </div>
        <div className="md:col-span-3 bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8">
          <VipForm />
        </div>
      </div>
    </section>
  );
};
